import React from 'react';
import { CheckCircle2, Search, Users } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { ScrollArea } from 'components/ui/scroll-area';
import { Switch } from 'components/ui/switch';
import { cn } from 'lib/utils';
import type { OrganizationAdminUserSummary } from 'types';

interface UserPickerProps {
  users: OrganizationAdminUserSummary[];
  selectedUserIds: number[];
  search: string;
  showSelectedOnly: boolean;
  onSearchChange: (value: string) => void;
  onShowSelectedOnlyChange: (value: boolean) => void;
  onToggleUser: (userId: number) => void;
  onSelectAllUsers: () => void;
  onDeselectAllUsers: () => void;
}

const getUserInitials = (displayName: string, email: string) => {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
};

export const UserPicker: React.FC<UserPickerProps> = ({
  users,
  selectedUserIds,
  search,
  showSelectedOnly,
  onSearchChange,
  onShowSelectedOnlyChange,
  onToggleUser,
  onSelectAllUsers,
  onDeselectAllUsers,
}) => {
  const visibleUserIds = users.map((user) => user.id);
  const visibleSelectedCount = visibleUserIds.filter((id) => selectedUserIds.includes(id)).length;
  const hasVisibleUsers = visibleUserIds.length > 0;
  const emptyTitle = showSelectedOnly ? 'No selected users in this view' : 'No users found';
  const emptyDescription = showSelectedOnly
    ? 'Clear the current search, or return to all users to select more.'
    : 'Try a different search term.';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Compare Users</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick specific users for the comparison table. Leave blank to export all users.
        </p>
      </div>

      <label className="relative block">
        <span className="sr-only">Search organization users</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search users..."
          className="pl-10"
        />
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {visibleSelectedCount} of {visibleUserIds.length} visible users selected
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <Switch
              id="response-user-show-selected"
              checked={showSelectedOnly}
              onCheckedChange={onShowSelectedOnlyChange}
            />
            <Label
              htmlFor="response-user-show-selected"
              className="cursor-pointer text-xs text-muted-foreground"
            >
              Show selected users
            </Label>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              type="button"
              variant="secondary"
              size="auto"
              className="px-3 py-2 text-xs"
              onClick={onSelectAllUsers}
              disabled={!hasVisibleUsers || visibleSelectedCount === visibleUserIds.length}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="auto"
              className="px-3 py-2 text-xs"
              onClick={onDeselectAllUsers}
              disabled={!hasVisibleUsers || visibleSelectedCount === 0}
            >
              Deselect all
            </Button>
          </div>
        </div>
      </div>

      <ScrollArea className="h-[26rem] pr-3">
        <div className="space-y-3">
          {users.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-muted/20 px-5 py-8 text-center">
              <Users className="mx-auto size-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium text-foreground">{emptyTitle}</p>
              <p className="mt-1 text-xs text-muted-foreground">{emptyDescription}</p>
            </div>
          ) : null}

          {users.map((user) => {
            const isSelected = selectedUserIds.includes(user.id);
            return (
              <button
                key={user.id}
                type="button"
                aria-label={`${isSelected ? 'Remove' : 'Select'} ${user.display_name}`}
                onClick={() => onToggleUser(user.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-3xl border px-4 py-3 text-left shadow-sm transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isSelected
                    ? 'border-foundation-orange-500 bg-foundation-orange-50'
                    : 'border-border bg-white hover:-translate-y-0.5 hover:border-foundation-orange-300 hover:shadow-elevation-sm'
                )}
              >
                <Avatar
                  className="size-10"
                  data-testid={`response-user-avatar-${user.id}`}
                  data-avatar-url={user.avatar_url ?? undefined}
                >
                  {user.avatar_url ? (
                    <AvatarImage
                      src={user.avatar_url}
                      alt={`${user.display_name} profile photo`}
                      className="object-cover"
                    />
                  ) : null}
                  <AvatarFallback className="bg-foundation-calm-blue-50 text-foundation-base-darkbase-dark-500 ring-1 ring-inset ring-foundation-calm-blue-200">
                    {getUserInitials(user.display_name, user.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{user.display_name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                <CheckCircle2
                  className={cn(
                    'size-5 shrink-0 transition-colors',
                    isSelected ? 'text-foundation-orange-500' : 'text-muted-foreground/30'
                  )}
                />
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
